import {
  Card,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Flex,
  useRadio,
  useRadioGroup,
  UseRadioProps,
  Box,
} from '@chakra-ui/react';
import React from 'react';
import { PathValue, useController, useFormContext } from 'react-hook-form';

import { colors } from 'packages/common-frontend/colors';
import { useSimpleColorMode } from 'packages/common-frontend/theme/utils';
import {
  StrictFieldValues,
  StringPath,
} from '~/core/routes/settings/implementations/common/Inputs/types';

type RadioCardProps = {
  radioProps: UseRadioProps;
  children: React.ReactNode;
  isError: boolean;
};

const RadioCard = ({ radioProps, children, isError }: RadioCardProps) => {
  const { getInputProps, getRadioProps } = useRadio(radioProps);
  const mode = useSimpleColorMode();
  const input = getInputProps();
  const cardProps = getRadioProps();

  return (
    <Box as="label">
      <input {...input} />
      <Card
        cursor="pointer"
        variant="outline"
        padding="1rem"
        size="sm"
        width="100%"
        borderColor={isError ? colors.red[500] : undefined}
        _checked={{
          borderColor: colors.blue[500],
          background: mode(colors.gray[50], colors.gray[800]),
        }}
        _focus={{
          boxShadow: 'outline',
        }}
        _hover={{
          background: mode(colors.gray[50], colors.gray[800]),
        }}
        {...cardProps}
      >
        {children}
      </Card>
    </Box>
  );
};

type RadioCardGroupProps<T extends StrictFieldValues> = {
  name: StringPath<T>;
  label?: string;
  options: {
    value: PathValue<T, StringPath<T>>;
    content: React.ReactNode;
  }[];
  helperText?: React.ReactNode;
  required?: boolean;
};

export const RadioCardGroup = <T extends StrictFieldValues>({
  name,
  label,
  options,
  helperText = '',
  required = false,
}: RadioCardGroupProps<T>) => {
  const { control } = useFormContext<T>();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: {
      required: required ? 'Select an option' : false,
    },
  });

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: field.name,
    value: String(field.value), // Ok to convert because `name: StringPath<T>` ensures we're dealing with string values
    onChange: field.onChange,
  });

  const group = getRootProps();

  return (
    <FormControl isInvalid={!!error}>
      {label && <FormLabel fontWeight="bold">{label}</FormLabel>}

      <Flex direction="column" gap="1rem" width="100%" {...group}>
        {options.map((option) => {
          const radio = getRadioProps({ value: option.value });
          return (
            <RadioCard
              key={String(option.value)}
              radioProps={radio}
              isError={!!error}
            >
              <Flex direction="column" gap="0.6rem">
                {option.content}
              </Flex>
            </RadioCard>
          );
        })}
      </Flex>

      {error && <FormErrorMessage>{error.message}</FormErrorMessage>}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
